import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Cookies from "universal-cookie";
import moment from "moment-timezone";
import { RingLoader } from "../_components/loaders/RingLoader";
import {
  pickBackUpEnrollment,
  resetProfilePassword,
  validateEnrollmentCode,
} from "../_api/enrolleeApi";
import { enrolleeService } from "../_services/enrollee.service";
import {
  enrolleeUpdate,
  loadEnrollee,
} from "../_redux/actions/enrolleeActions";
import { loadVehicle } from "../_redux/actions/vehicleActions";
import {
  Guardian,
  LifeSafer,
  Monitech,
  QuickStart,
} from "../_helpers/BrandConstants";

const STORY_STEPS = {
  pricing: ["Welcome", "Contact", "Violation", "Vehicle", "Build My Quote"],
  schedule: [
    "Welcome",
    "Contact",
    "Violation",
    "Address",
    "Id",
    "Vehicle",
    // "Lpp",
    "Ready",
    "Schedule",
    "MyAccount",
  ],
  speed: [
    "Welcome",
    "Contact",
    "LearnMore",
    "Driver",
    "WhyIsa",
    "Pricing",
    "Vehicle",
    "Ready",
    "Schedule",
    "Confirm",
    "WrapUp",
  ],
  location: [
    "Schedule",
    "Contact",
    "Violation",
    "Vehicle",
    "StateId",
    "Address",
    "MyAccount",
  ],
  partner: [
    "Welcome",
    "Contact",
    "Violation",
    "Vehicle",
    "Address",
    "Id",
    "PartnerPricing",
    "Schedule",
    "MyAccount",
  ],
  europe: ["Welcome", "Contact", "Vehicle", "Address", "Schedule"],
};

// const LINK_EXPIRATION_DAYS = null;
const PASSWORD_PATTERN =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,24}$/;

// const STEP_LABELS = {
//   Welcome: "Welcome",
//   Contact: "Your information",
//   Violation: "Program information",
//   Vehicle: "Vehicle information",
//   Address: "Home address",
//   Id: "Identification",
//   Lpp: "Protection plan",
//   Ready: "Review",
//   Schedule: "Schedule your appointment",
//   MyAccount: "Create your account",
//   LearnMore: "Learn more",
//   Driver: "Driver information",
//   WhyIsa: "Why ISA",
//   Pricing: "Pricing",
//   Confirm: "Confirmation",
//   WrapUp: "Final details",
//   StateId: "Identification",
//   PartnerPricing: "Partner pricing",
//   "Build My Quote": "Build your quote",
// };

const normalizeStory = (story) => {
  const normalized = String(story || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;

  const compactStory = normalized.replace(/[^a-z0-9]/g, "");
  if (["schedulenow", "schedule"].includes(compactStory)) return "schedule";
  if (["isavolunteer", "speed"].includes(compactStory)) return "speed";
  if (normalized === "partner") return "partner";
  if (normalized === "location") return "location";
  if (normalized === "pricing") return "pricing";
  if (normalized === "europe") return "europe";
  return "pricing";
};

const getNextStep = (story, completedStep) => {
  const steps = STORY_STEPS[story] || STORY_STEPS.pricing;
  const completedIndex = steps.findIndex(
    (step) =>
      step.replace(/\s+/g, "").toLowerCase() ===
      String(completedStep || "")
        .replace(/\s+/g, "")
        .toLowerCase(),
  );
  if (completedIndex < 0) return steps[0];
  return steps[Math.min(completedIndex + 1, steps.length - 1)];
};

const getValue = (source, ...keys) => {
  if (!source || typeof source !== "object") return null;

  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }

  const normalizedKeys = keys.map((key) =>
    String(key)
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase(),
  );
  const matchingKey = Object.keys(source).find((key) =>
    normalizedKeys.includes(
      String(key)
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase(),
    ),
  );

  if (matchingKey !== undefined) return source[matchingKey];

  return null;
};

const asBoolean = (value) =>
  value === true ||
  value === 1 ||
  String(value ?? "")
    .trim()
    .toLowerCase() === "true" ||
  String(value ?? "").trim() === "1";

const normalizeStep = (step) =>
  String(step || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const isAtOrAfterVehicleBeforeLastStep = (story, completedStep) => {
  const steps = STORY_STEPS[story] || [];
  const completedIndex = steps.findIndex(
    (step) => normalizeStep(step) === normalizeStep(completedStep),
  );
  const vehicleIndex = steps.findIndex(
    (step) => normalizeStep(step) === "vehicle",
  );

  return (
    vehicleIndex >= 0 &&
    completedIndex >= vehicleIndex &&
    completedIndex < steps.length - 1
  );
};

const getVehicleId = (vehicle) =>
  getValue(vehicle, "vehicleId", "VehicleId", "vehicleID", "VehicleID");

const getPickupStatus = (step, scheduledStatus = null) => {
  if (scheduledStatus !== null) {
    return scheduledStatus ? "scheduled" : "ready";
  }

  switch (normalizeStep(step)) {
    case "appointment":
    case "clientportal":
    case "schedule":
    case "myaccount":
      return "scheduled";
    case "":
      return "ready";
    default:
      return "ready";
  }
};

const hasExpired = (source) => {
  if (
    asBoolean(
      getValue(source, "linkExpired", "isExpired", "expired", "tokenExpired"),
    )
  ) {
    return true;
  }

  const expiresAt = getValue(
    source,
    "expiresAt",
    "expirationDate",
    "expiresOn",
    "tokenExpirationDate",
  );
  if (!expiresAt) return false;

  const expirationTime = new Date(expiresAt).getTime();
  return !Number.isNaN(expirationTime) && expirationTime <= Date.now();
};

const getOptionalBoolean = (source, ...keys) => {
  const value = getValue(source, ...keys);
  return value === null ? null : asBoolean(value);
};

const resolveHasMyAccount = (source) => {
  const explicitStatus = getOptionalBoolean(
    source,
    "hasMyAccount",
    "myAccountExists",
    "accountExists",
    "hasAccountLogin",
    "hasPassword",
    "hasCreatedPassword",
    "passwordCreated",
    "isExistingAccount",
    "loginExists",
  );
  if (explicitStatus !== null) return explicitStatus;

  const accountStatus = String(
    getValue(source, "accountStatus", "myAccountStatus") || "",
  )
    .trim()
    .toLowerCase();
  if (["active", "created", "existing"].includes(accountStatus)) return true;
  if (["new", "notcreated", "pending"].includes(accountStatus)) return false;

  const isNewUser = getOptionalBoolean(source, "isNewUser");
  if (isNewUser !== null) return !isNewUser;

  const passwordChangeDate = getValue(source, "passwordChangeDate");
  if (passwordChangeDate) {
    const parsedDate = new Date(passwordChangeDate);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.getUTCFullYear() > 1901;
    }
  }

  return null;
};

const resolveAccountUrl = (source, hasMyAccount) => {
  const configuredUrl = process.env.CP_URL;
  const responseUrl = getValue(
    source,
    hasMyAccount ? "loginUrl" : "createAccountUrl",
    hasMyAccount ? "accountLoginUrl" : "accountCreationUrl",
    "myAccountUrl",
    "accountUrl",
    "accountRedirectUrl",
  );

  if (!responseUrl) return hasMyAccount ? configuredUrl : null;

  try {
    const allowedOrigin = new URL(configuredUrl).origin;
    const resolvedUrl = new URL(responseUrl, allowedOrigin);
    return resolvedUrl.origin === allowedOrigin
      ? resolvedUrl.toString()
      : configuredUrl;
  } catch {
    return configuredUrl;
  }
};

const unwrapResponse = (response) => {
  if (Array.isArray(response)) return response[0] || null;

  const nestedResponse = getValue(response, "data", "result");
  if (Array.isArray(nestedResponse)) return nestedResponse[0] || null;
  if (nestedResponse && typeof nestedResponse === "object") {
    return nestedResponse;
  }

  return response;
};

const PickBackUpEnrollment = ({
  enrolleeUpdate,
  loadEnrollee,
  loadVehicle,
}) => {
  const { token, tokenId } = useParams();
  const pickupToken = token || tokenId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cookies = useMemo(() => new Cookies(), []);
  const [status, setStatus] = useState("loading");
  const [resumeData, setResumeData] = useState(null);
  // const [expiredDays, setExpiredDays] = useState(LINK_EXPIRATION_DAYS);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const requestStartedRef = useRef(false);

  const setCookie = (name, value, minutes = 30) => {
    cookies.set(name, value, {
      path: "/",
      sameSite: "strict",
      expires: moment().add(minutes, "minutes").toDate(),
    });
  };

  const resolveBrand = () => {
    const url = window.location.href.toLowerCase();
    return (
      [LifeSafer, Monitech, Guardian, QuickStart].find((brand) =>
        url.includes(brand.toLowerCase()),
      ) ||
      cookies.get("lsEnrollmentReferral") ||
      LifeSafer
    );
  };

  const saveLinkContext = (response) => {
    const campaignId = getValue(
      response,
      "enrollmentCampaignID",
      "enrollmentCampaignId",
    );
    const linkTypeId = getValue(response, "linkTypeID", "linkTypeId");
    const linkId = getValue(response, "linkID", "linkId");
    if (campaignId) setCookie("lsenrollmentCampaignID", campaignId);
    if (linkTypeId) setCookie("lslinkTypeID", linkTypeId);
    if (linkId) setCookie("lslinkID", linkId);
  };

  const saveEnrollmentCode = async (response, enrollee, brand) => {
    const responseCode = getValue(response, "enrollmentCode", "EnrollmentCode");
    const code =
      responseCode || getValue(enrollee, "enrollmentCode", "EnrollmentCode");
    const enrollmentCodeId =
      getValue(
        response,
        "enrollmentCodeId",
        "enrollmentCodeID",
        "EnrollmentCodeId",
        "EnrollmentCodeID",
      ) ||
      getValue(
        enrollee,
        "enrollmentCodeId",
        "enrollmentCodeID",
        "EnrollmentCodeId",
        "EnrollmentCodeID",
      );

    if (code && typeof code === "object") {
      setCookie("lsEnrollmentCode", code);
      return code;
    }

    if (code) {
      try {
        const validatedCode = await validateEnrollmentCode(code, brand);
        setCookie("lsEnrollmentCode", validatedCode);
        return validatedCode;
      } catch {
        const fallbackCode = { enrollmentCode: code, enrollmentCodeId };
        setCookie("lsEnrollmentCode", fallbackCode);
        return fallbackCode;
      }
    }

    if (enrollmentCodeId) {
      const fallbackCode = { enrollmentCodeId };
      setCookie("lsEnrollmentCode", fallbackCode);
      return fallbackCode;
    }

    return null;
  };

  useEffect(() => {
    if (requestStartedRef.current) return;
    requestStartedRef.current = true;

    const validateAndLoad = async () => {
      const brand = resolveBrand();

      try {
        if (!pickupToken) {
          setStatus("expired");
          return;
        }

        const apiResponse = await pickBackUpEnrollment(pickupToken, brand);
        const response = unwrapResponse(apiResponse);
        const isValid = getValue(response, "isValid", "valid", "Valid");
        const returnedStep = getValue(response, "step", "Step");
        const responseScheduledStatus = getOptionalBoolean(
          response,
          "scheduled",
          "hasAppointment",
          "appointmentScheduled",
        );
        const enrolleeId = getValue(response, "enrolleeId");
        const userId = getValue(response, "userId");
        const responseClientId = getValue(response, "clientId");

        // setExpiredDays(
        //   getValue(response, "daysSinceSent", "expiredDays", "tokenAgeDays") ||
        //     LINK_EXPIRATION_DAYS,
        // );

        if (
          !response ||
          hasExpired(response) ||
          (isValid !== null && !asBoolean(isValid)) ||
          !enrolleeId ||
          !userId
        ) {
          setStatus("expired");
          return;
        }

        saveLinkContext(response);

        const verified = await enrolleeService.enrolleeVerify({
          userId,
          resetCode: getValue(response, "resetCode", "ResetCode"),
          brandIdentifier: brand,
        });
        const clientId = getValue(verified, "clientId") || responseClientId;

        if (!clientId) {
          setStatus("expired");
          return;
        }

        const auth = {
          ...verified,
          enrolleeId,
          userId: getValue(verified, "userId", "userID") || userId,
          clientId,
          hasToken: true,
          jwtToken: getValue(verified, "jwtToken", "JWTToken"),
          JWTToken: getValue(verified, "jwtToken", "JWTToken"),
          refreshToken: getValue(verified, "refreshToken", "RefreshToken"),
          RefreshToken: getValue(verified, "refreshToken", "RefreshToken"),
        };
        setCookie("lifesaferEnrollment", auth);

        const enrollee = await loadEnrollee(clientId, auth.userId);
        const enrollmentCode = await saveEnrollmentCode(
          response,
          enrollee,
          brand,
        );
        const story = normalizeStory(
          getValue(response, "story", "Story") ||
            getValue(enrollee, "story", "Story"),
        );
        const completedStep =
          getValue(enrollee, "step", "Step") || returnedStep;
        const enrolleeScheduledStatus = getOptionalBoolean(
          enrollee,
          "scheduled",
          "hasAppointment",
          "appointmentScheduled",
        );
        const scheduledStatus =
          responseScheduledStatus ?? enrolleeScheduledStatus;
        const hasMyAccount =
          normalizeStep(completedStep) === "clientportal" ||
          (resolveHasMyAccount(response) ??
            resolveHasMyAccount(verified) ??
            resolveHasMyAccount(enrollee) ??
            false);
        const pickupStatus = getPickupStatus(completedStep, scheduledStatus);
        const isScheduled = pickupStatus === "scheduled";
        const shouldResumeScheduling =
          responseScheduledStatus === false &&
          ["appointment", "clientportal", "schedule", "myaccount"].includes(
            normalizeStep(completedStep),
          );
        let nextStep = shouldResumeScheduling
          ? "Schedule"
          : normalizeStep(completedStep) === "clientportal"
            ? "Client Portal"
            : getNextStep(story, completedStep);
        let selectedVehicle = null;
        let requiresVehicleSelection = false;
        const rawClientTypeId = getValue(
          enrollee,
          "clientTypeId",
          "ClientTypeId",
        );
        const clientTypeId =
          rawClientTypeId === null ? null : Number(rawClientTypeId);
        const rawDeviceType = String(
          getValue(enrollee, "deviceType", "DeviceType") || "",
        ).toLowerCase();
        const deviceType =
          clientTypeId === 5 || ["pam", "portable"].includes(rawDeviceType)
            ? "Portable"
            : clientTypeId === 6 ||
                ["isa", "speed", "speedv"].includes(rawDeviceType)
              ? "Speed"
              : clientTypeId === 1 ||
                  ["iid", "interlock"].includes(rawDeviceType)
                ? "Interlock"
                : null;
        const isPortable = clientTypeId === 5 || deviceType === "Portable";
        const shouldContinueAfterVehicle =
          story === "schedule" &&
          responseScheduledStatus === false &&
          normalizeStep(completedStep) === "clientportal";

        if (
          !isScheduled &&
          !isPortable &&
          (shouldResumeScheduling ||
            isAtOrAfterVehicleBeforeLastStep(story, completedStep))
        ) {
          try {
            const vehicleData = await loadVehicle(clientId, auth.userId);
            const vehicles = Array.isArray(vehicleData)
              ? vehicleData.filter((vehicle) => getVehicleId(vehicle))
              : [];

            if (vehicles.length === 1) {
              selectedVehicle = vehicles[0];
              if (shouldContinueAfterVehicle) {
                nextStep = getNextStep(story, "Vehicle");
              }
            } else {
              nextStep = "Vehicle";
              requiresVehicleSelection = true;
            }
          } catch {
            nextStep = "Vehicle";
            requiresVehicleSelection = true;
          }
        }

        const selectedVehicleId = getVehicleId(selectedVehicle);
        const selectedVehicleData = selectedVehicle
          ? { ...selectedVehicle }
          : {};
        delete selectedVehicleData.userId;
        const enrollmentCodeId =
          getValue(enrollmentCode, "enrollmentCodeId") ||
          getValue(response, "enrollmentCodeId") ||
          getValue(enrollee, "enrollmentCodeId");
        const hydratedEnrollee = {
          ...enrollee,
          ...selectedVehicleData,
          ...auth,
          enrolleeId,
          userId: auth.userId,
          clientId,
          ...(clientTypeId !== null && !Number.isNaN(clientTypeId)
            ? { clientTypeId }
            : {}),
          ...(deviceType ? { deviceType } : {}),
          story,
          step: nextStep,
          Step: nextStep,
          pickupCompletedStep: completedStep,
          fromPickBackUpLink: true,
          scheduled: isScheduled,
          hasMyAccount,
          ...(selectedVehicleId
            ? {
                vehicleId: selectedVehicleId,
                VehicleId: selectedVehicleId,
                VehicleID: selectedVehicleId,
                selectedVehicleID: selectedVehicleId,
                selectedVehicleId,
                partnerVehicleSelectionConfirmed: story === "partner",
                europeVehicleSelectionConfirmed: story === "europe",
              }
            : {}),
          ...(requiresVehicleSelection
            ? {
                vehicleId: null,
                VehicleId: null,
                VehicleID: null,
                selectedVehicleID: null,
                selectedVehicleId: null,
                partnerVehicleSelectionConfirmed: false,
                europeVehicleSelectionConfirmed: false,
              }
            : {}),
          ...(enrollmentCodeId ? { enrollmentCodeId } : {}),
        };

        if (story === "partner" && enrollmentCode) {
          setCookie("lsEnrollmentCode", {
            ...enrollmentCode,
            enrollmentCodeType:
              getValue(enrollmentCode, "enrollmentCodeType") || "Partner",
          });
        }

        sessionStorage.setItem(
          "enrolleeUser",
          JSON.stringify(hydratedEnrollee),
        );
        setCookie("lifesaferEnrollment", hydratedEnrollee);
        setCookie("lsPickBackUp", { fromPickBackUpLink: true });

        if (!story) {
          navigate("/pick-a-story", { replace: true });
          return;
        }

        setResumeData({
          firstName:
            getValue(enrollee, "firstName", "FirstName") ||
            getValue(response, "firstName", "FirstName") ||
            "",
          story,
          nextStep,
          isScheduled,
          hasMyAccount,
          accountUrl: resolveAccountUrl(response, hasMyAccount),
          email:
            getValue(enrollee, "email", "Email", "emailAddress") ||
            getValue(response, "email", "Email", "emailAddress"),
          phone:
            getValue(
              enrollee,
              "cellPhone",
              "CellPhone",
              "phoneNumber",
              "PhoneNumber",
              "homePhone",
              "HomePhone",
            ) ||
            getValue(
              response,
              "cellPhone",
              "CellPhone",
              "phoneNumber",
              "PhoneNumber",
              "homePhone",
              "HomePhone",
            ),
          userId: auth.userId,
          clientId,
          brand,
        });
        setStatus(pickupStatus);
      } catch (error) {
        // const message = String(error?.message || "");
        // const match = message.match(/(\d+)\s*days?/i);
        // if (match) setExpiredDays(Number(match[1]));
        setStatus("expired");
      }
    };

    validateAndLoad();
  }, [pickupToken]);

  const restartEnrollment = () => {
    sessionStorage.removeItem("enrolleeUser");
    sessionStorage.removeItem("appointmentData");
    sessionStorage.removeItem("buildQuoteStep");
    cookies.remove("lifesaferEnrollment", { path: "/" });
    cookies.remove("lsEnrollmentCode", { path: "/" });
    cookies.remove("lsPickBackUp", { path: "/" });
    navigate("/entry", { replace: true });
  };

  const continueEnrollment = () => {
    if (!resumeData?.story) return;
    navigate(`/story/${resumeData.story}`, { replace: true });
  };

  const transferToAccount = async () => {
    const phoneNumber = String(resumeData?.phone || "").replace(/\D/g, "");
    if (!resumeData?.email && !phoneNumber) {
      throw new Error("An email address or phone number is required.");
    }

    const transferCode = await enrolleeService.sendVerifyCode({
      emailAddress: resumeData?.email || null,
      phoneNumber: resumeData?.email ? null : phoneNumber,
      isPhone: !resumeData?.email,
      brandIdentifier:
        resumeData?.brand === Monitech ? "monitechiid" : resumeData?.brand,
      silent: true,
    });
    const transferPath = [
      transferCode,
      resumeData?.userId,
      resumeData?.clientId,
    ]
      .map((value) => encodeURIComponent(value))
      .join("-");

    window.location.assign(
      `${process.env.CP_URL}/account/transfer/${transferPath}`,
    );
  };

  const continueToAccount = async () => {
    if (resumeData?.hasMyAccount) {
      window.location.assign(process.env.CP_URL);
      return;
    }

    if (resumeData?.accountUrl) {
      window.location.assign(resumeData.accountUrl);
      return;
    }

    if (
      (!resumeData?.email && !resumeData?.phone) ||
      !resumeData?.userId ||
      !resumeData?.clientId
    ) {
      window.location.assign(process.env.CP_URL);
      return;
    }

    setIsRedirecting(true);
    try {
      await transferToAccount();
    } catch {
      window.location.assign(process.env.CP_URL);
    }
  };

  const validatePassword = (value) => {
    if (!value) return t("location.passwordRequired");
    if (!PASSWORD_PATTERN.test(value)) return t("location.passwordPattern");
    return "";
  };

  const validateConfirmPassword = (value, currentPassword) => {
    if (!value) return t("location.confirmPasswordRequired");
    if (value !== currentPassword) return t("location.confirmPasswordValid");
    return "";
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    setPasswordErrors((currentErrors) => ({
      ...currentErrors,
      password: validatePassword(value),
      confirmPassword: validateConfirmPassword(confirmPassword, value),
    }));
  };

  const handleConfirmPasswordChange = (event) => {
    const value = event.target.value;
    setConfirmPassword(value);
    setPasswordErrors((currentErrors) => ({
      ...currentErrors,
      confirmPassword: validateConfirmPassword(value, password),
    }));
  };

  const createAccount = async (event) => {
    event.preventDefault();
    const errors = {};

    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(
      confirmPassword,
      password,
    );
    if (passwordError) errors.password = passwordError;
    if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsRedirecting(true);
    try {
      await resetProfilePassword(resumeData?.userId, password);
      const currentEnrollee =
        JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
      const myAccountUpdate = {
        enrolleeId: currentEnrollee?.enrolleeId || currentEnrollee?.EnrolleeId,
        clientId: resumeData?.clientId,
        Step: "Client Portal",
        complete: true,
        hasMyAccount: true,
        story: resumeData?.story,
        deviceType: currentEnrollee?.deviceType || currentEnrollee?.DeviceType,
        enrollmentCodeId:
          currentEnrollee?.enrollmentCodeId ||
          currentEnrollee?.EnrollmentCodeId ||
          currentEnrollee?.EnrollmentCodeID,
      };

      await enrolleeUpdate(myAccountUpdate);
      sessionStorage.setItem(
        "enrolleeUser",
        JSON.stringify({
          ...currentEnrollee,
          ...myAccountUpdate,
          step: "Client Portal",
          hasMyAccount: true,
        }),
      );

      try {
        await transferToAccount();
      } catch {
        window.location.assign(`${process.env.CP_URL}/account/login`);
      }
    } catch (error) {
      setPasswordErrors({
        submit: error?.message || t("toasts.updateFailed"),
      });
      setIsRedirecting(false);
    }
  };

  if (status === "loading") {
    return (
      <Grid
        sx={{
          flex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RingLoader />
      </Grid>
    );
  }

  const isExpired = status === "expired";
  const isScheduled = status === "scheduled";
  // const nextStepLabel =
  //   STEP_LABELS[resumeData?.nextStep] || resumeData?.nextStep;

  return (
    <Grid
      component="main"
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: { xs: "calc(100vh - 56px)", sm: "100vh" },
        mt: { xs: "56px", sm: 0 },
        px: { xs: 2.25, sm: 5, md: 8 },
        pt: { xs: 8, sm: 7 },
        pb: { xs: 12, sm: 5 },
        textAlign: isExpired ? "center" : "left",
      }}
    >
      <Grid
        sx={{
          width: "100%",
          maxWidth: isExpired ? 760 : 720,
          mx: "auto",
        }}
      >
        {isExpired ? (
          <>
            <Typography
              component="h1"
              sx={{
                color: "#4b9cd3",
                fontWeight: 700,
                fontSize: 24,
              }}
            >
              {t("pickup.expiredTitle")}
            </Typography>
            <Typography sx={{ mt: 1.5, lineHeight: 1.5 }}>
              {t("pickup.newexpireDescription")}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={restartEnrollment}
              sx={{
                mt: 2.5,
                maxWidth: 380,
                bgcolor: "#76bc21",
                color: "#1c355e",
                fontWeight: 600,
                "&:hover": { bgcolor: "#65a91d" },
              }}
            >
              {t("pickup.restart")}
            </Button>
          </>
        ) : (
          <>
            <Typography
              component="h1"
              sx={{
                color: "#4b9cd3",
                fontWeight: 700,
                fontSize: { xs: 24 },
              }}
            >
              {t(isScheduled ? "pickup.welcomeBack" : "pickup.welcome", {
                name: resumeData?.firstName,
              })}
            </Typography>
            <Typography sx={{ mt: 0.75, lineHeight: 1.55 }}>
              {t(
                isScheduled
                  ? resumeData?.hasMyAccount
                    ? "pickup.scheduledExistingAccount"
                    : "pickup.scheduledNewAccount"
                  : "pickup.progressSaved",
              )}
            </Typography>
            {/* <Typography sx={{ mt: 2, fontWeight: 700 }}>
              {nextStepLabel}
            </Typography> */}
            {!isScheduled && (
              <Typography sx={{ mt: 3 }}>
                {t("pickup.fewMoreMinutes")}
              </Typography>
            )}
            {isScheduled && !resumeData?.hasMyAccount ? (
              <Paper
                elevation={2}
                sx={{
                  mt: 4,
                  p: { xs: 2, sm: 3 },
                  maxWidth: 620,
                  bgcolor: "#eef6ff",
                }}
              >
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                  {t("pickup.createAccountTitle")}
                </Typography>
                <Typography sx={{ mt: 1, lineHeight: 1.45 }}>
                  {t("pickup.createAccountDescription")}
                </Typography>
                <form onSubmit={createAccount}>
                  <Typography sx={{ mt: 2.5, mb: 0.75, fontWeight: 700 }}>
                    {t("pickup.createPassword")}
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    placeholder={t("location.password")}
                    value={password}
                    onChange={handlePasswordChange}
                    error={Boolean(passwordErrors.password)}
                    helperText={passwordErrors.password}
                    disabled={isRedirecting}
                    sx={{ bgcolor: "white" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("pickup.togglePassword")}
                            onClick={() =>
                              setShowPassword((visible) => !visible)
                            }
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}>
                    {t("pickup.confirmPassword")}
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    placeholder={t("location.password")}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    error={Boolean(passwordErrors.confirmPassword)}
                    helperText={passwordErrors.confirmPassword}
                    disabled={isRedirecting}
                    sx={{ bgcolor: "white" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("pickup.togglePassword")}
                            onClick={() =>
                              setShowPassword((visible) => !visible)
                            }
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {passwordErrors.submit && (
                    <Typography color="error" sx={{ mt: 1 }}>
                      {passwordErrors.submit}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isRedirecting}
                    sx={{
                      mt: 2.5,
                      minHeight: 52,
                      bgcolor: "#1c3d6e",
                      // fontSize: 18,
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#142f57" },
                    }}
                  >
                    {t("pickup.createAccount")}
                  </Button>
                </form>
              </Paper>
            ) : (
              <Grid
                sx={{
                  position: { xs: "fixed", sm: "static" },
                  left: { xs: 0, sm: "auto" },
                  right: { xs: 0, sm: "auto" },
                  bottom: { xs: 0, sm: "auto" },
                  mt: { sm: 5 },
                  p: { xs: 1.5, sm: 0 },
                  bgcolor: { xs: "white", sm: "transparent" },
                  boxShadow: {
                    xs: "0 -2px 12px rgba(0,0,0,0.14)",
                    sm: "none",
                  },
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  onClick={isScheduled ? continueToAccount : continueEnrollment}
                  disabled={isRedirecting}
                  sx={{
                    maxWidth: { sm: 300 },
                    // minHeight: 52,
                    bgcolor: isScheduled ? "#1c355e" : "#76bc21",
                    color: isScheduled ? "white" : "#1c355e",
                    // fontSize: 16,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: isScheduled ? "#1c355e" : "#65a91d",
                    },
                  }}
                >
                  {t(isScheduled ? "pickup.logInToAccount" : "pickup.continue")}
                </Button>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

PickBackUpEnrollment.propTypes = {
  enrolleeUpdate: PropTypes.func.isRequired,
  loadEnrollee: PropTypes.func.isRequired,
  loadVehicle: PropTypes.func.isRequired,
};

export default connect(null, { enrolleeUpdate, loadEnrollee, loadVehicle })(
  PickBackUpEnrollment,
);
